import { ORG_EXTRA, type OrgData, type OrgExtra, type OrgOverview, type OrgLabels, type OrgBusinessRecord } from './organisationFixtures';
import { fetchOrganisations, updateOrganisation } from './organisationHttpClient';
import type { OrgDetailsDto, ShortOrgDetailsDto, UpdateOrgRequestDto } from './organisationApiTypes';
import type { SaveOrganisationResult } from './organisationDetailsApi';

const REGION_LABELS: Record<string, OrgLabels> = {
  AU: {
    number: 'ABN/Business number',
    eft: 'PAYG EFT code',
    withholding: 'PAYG witholding (days)',
    reportingTitle: 'STP Reporting',
    payrollTitle: 'Payrolls',
    bmsId: 'BMS ID',
    branch2: 'ATO branch',
    ssid: 'SSID (Sender)',
  },
  GB: {
    number: 'Company number',
    eft: 'PAYE reference',
    withholding: 'Tax period (week)',
    reportingTitle: 'RTI / HMRC',
    payrollTitle: 'Payrolls',
    bmsId: 'Accounts Office ref',
    branch2: 'HMRC office no.',
    ssid: 'Sender ID',
  },
  NZ: {
    number: 'NZBN',
    eft: 'IRD number',
    withholding: 'Pay period (days)',
    reportingTitle: 'Payday filing',
    payrollTitle: 'Payrolls',
    bmsId: 'myIR account',
    branch2: 'IR dept',
    ssid: 'Gateway ID',
  },
};

const DEFAULT_LABELS: OrgLabels = REGION_LABELS.AU;

const REGION_COLORS: Record<string, string> = {
  AU: '#a02050',
  GB: '#1e4d8b',
  NZ: '#065f46',
};

const deriveBadge = (tradingName?: string, id?: string): string => {
  if (tradingName) {
    const initials = tradingName.split(/\s+/).map((w) => w[0]?.toUpperCase() ?? '').join('');
    return initials.slice(0, 3) || (id?.slice(0, 3).toUpperCase() ?? '?');
  }
  return id?.slice(0, 3).toUpperCase() ?? '?';
};

const formatAddress = (addr?: { street?: string; suburb?: string; state?: string; postcode?: string }): string => {
  if (!addr) return '—';
  return [addr.street, addr.suburb, addr.state, addr.postcode].filter(Boolean).join(', ') || '—';
};

const mapChildOrg = (child: ShortOrgDetailsDto): OrgBusinessRecord => ({
  orgId: child.orgId,
  name: child.legalName ?? '—',
  badge: deriveBadge(child.tradingName, child.orgId),
  legal: {
    country: child.regionCode ?? '—',
    number: '—',
    branch: '—',
    legalName: child.legalName ?? '—',
    tradingName: child.tradingName ?? '—',
    taxSignatory: '—',
    eft: '—',
    withholding: '—',
    regionalTax: false,
  },
  stp: {
    bmsId: '—',
    reportingParty: '—',
    branch2: '—',
    ssid: '—',
    phase: '—',
    lastSubmission: '—',
    tone: 'brand',
  },
  payrolls: [],
});

export const mapOrgDetailsToOrgData = (org: OrgDetailsDto): OrgData => ({
  label: org.legalName ?? org.tradingName ?? org.id ?? '—',
  badge: deriveBadge(org.tradingName, org.id),
  color: REGION_COLORS[org.regionCode ?? ''] ?? '#505050',
  labels: REGION_LABELS[org.regionCode ?? ''] ?? DEFAULT_LABELS,
  overview: {
    identity: {
      name: org.legalName ?? '—',
      shortCode: org.tradingName ?? '—',
      country: org.regionCode ?? '',
      fyEnd: '—',
      hqAddress: formatAddress(org.registeredBusinessAddress),
    },
    contact: {
      primary: org.primaryContact?.name ?? '—',
      phone: org.primaryContact?.phone ?? '—',
      supportEmail: org.primaryContact?.email ?? '—',
      senderEmail: '—',
    },
    defaults: {
      timezone: org.defaultTimezone ?? '',
      currency: org.defaultCurrency ?? '',
    },
    callout: { icon: 'info', tone: 'brand', title: 'Organisation details', text: '' },
  },
  businessRecords: (org.childOrgs ?? []).map(mapChildOrg),
});

const orgDetailsCache = new Map<string, OrgDetailsDto>();

export const listOrganisations = async (): Promise<{ orgIds: string[]; orgs: Record<string, OrgData> }> => {
  const response = await fetchOrganisations();
  const dtos = response.result ?? [];
  orgDetailsCache.clear();
  const orgs: Record<string, OrgData> = {};
  const orgIds: string[] = [];
  for (const dto of dtos) {
    if (!dto.id) continue;
    orgDetailsCache.set(dto.id, dto);
    orgs[dto.id] = mapOrgDetailsToOrgData(dto);
    orgIds.push(dto.id);
  }
  return { orgIds, orgs };
};

export const getOrganisationExtra = async (orgId: string): Promise<OrgExtra> => {
  await Promise.resolve();
  return ORG_EXTRA[orgId] ?? { bankAccounts: [], payAdvice: { template: '—', delivery: [], showYtd: false, showLeave: false, footer: '' } };
};

const overviewToUpdateRequest = (orgId: string, overview: Partial<OrgOverview>, cached?: OrgDetailsDto): UpdateOrgRequestDto => ({
  id: orgId,
  legalName: overview.identity?.name ?? cached?.legalName,
  tradingName: overview.identity?.shortCode ?? cached?.tradingName,
  abn: cached?.abn,
  acn: cached?.acn,
  isStpReporter: cached?.isStpReporter ?? false,
  primaryContact: {
    name: overview.contact?.primary ?? cached?.primaryContact?.name,
    email: overview.contact?.supportEmail ?? cached?.primaryContact?.email,
    phone: overview.contact?.phone ?? cached?.primaryContact?.phone,
    role: cached?.primaryContact?.role,
  },
  registeredBusinessAddress: {
    street: overview.identity?.hqAddress ?? cached?.registeredBusinessAddress?.street,
    suburb: cached?.registeredBusinessAddress?.suburb,
    state: cached?.registeredBusinessAddress?.state,
    postcode: cached?.registeredBusinessAddress?.postcode,
    country: cached?.registeredBusinessAddress?.country,
  },
  branding: cached?.branding,
  effectiveFrom: cached?.effectiveFrom,
  retroactiveReason: cached?.retroactiveReason,
});

export const saveOrganisationOverview = async (orgId: string, overview: Partial<OrgOverview>): Promise<SaveOrganisationResult> => {
  try {
    const cached = orgDetailsCache.get(orgId);
    const request = overviewToUpdateRequest(orgId, overview, cached);
    await updateOrganisation(request);
    return { ok: true };
  } catch {
    return { ok: false };
  }
};
